import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  Bookmark,
  Scale,
  Loader2,
  Filter,
  BookmarkCheck,
  X,
  ChevronDown,
  ChevronUp,
  Gavel,
  Building2,
  CalendarDays,
  User,
  FileText,
  Hash,
  BookOpen,
  MessageSquare,
  FolderPlus,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

type JurisprudenciaTab = "pesquisar" | "salvas";

interface Tribunal {
  id: string;
  nome: string;
  sigla: string;
}

interface JurisprudenciaItem {
  id: string;
  numero_processo: string;
  ementa: string;
  relator: string | null;
  data_julgamento: string | null;
  orgao_julgador: string | null;
  area_direito: string | null;
  palavras_chave: string[];
  texto_completo: string | null;
  metadata: Record<string, any>;
  created_at: string;
  tribunais: { sigla: string; nome: string } | null;
}

// Extract relator from texto_completo when relator field is null
const extractRelatorFromTexto = (texto: string | null): string | null => {
  if (!texto) return null;
  // Match "Relator:" or "Relator(a):" or "RELATOR(A)" patterns
  const match = texto.match(/Relator(?:\(a\))?:\s*(.+?)(?:\n|$)/i);
  return match?.[1]?.trim() || null;
};

const getRelator = (item: JurisprudenciaItem): string => {
  return item.relator || extractRelatorFromTexto(item.texto_completo) || "Não informado";
};

export default function Jurisprudencias() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<JurisprudenciaTab>("pesquisar");
  const [searchQuery, setSearchQuery] = useState("");
  const [tribunalFilter, setTribunalFilter] = useState("all");
  const [tribunais, setTribunais] = useState<Tribunal[]>([]);
  const [results, setResults] = useState<JurisprudenciaItem[]>([]);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [savedItems, setSavedItems] = useState<JurisprudenciaItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<JurisprudenciaItem | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showCasoDialog, setShowCasoDialog] = useState<JurisprudenciaItem | null>(null);
  const [casos, setCasos] = useState<{ id: string; title: string }[]>([]);
  const [addingToCaso, setAddingToCaso] = useState(false);
  const [loadingCasos, setLoadingCasos] = useState(false);

  // Load tribunais
  useEffect(() => {
    supabase.from("tribunais").select("id, nome, sigla").order("sigla").then(({ data }) => {
      if (data) setTribunais(data);
    });
  }, []);

  // Deep link: open jurisprudencia by ?id= query param
  useEffect(() => {
    const deepLinkId = searchParams.get("id");
    if (deepLinkId) {
      supabase
        .from("jurisprudencias")
        .select("*, tribunais(sigla, nome)")
        .eq("id", deepLinkId)
        .single()
        .then(({ data }) => {
          if (data) {
            setSelectedItem(data as any);
            if (user) {
              supabase.from("jurisprudencia_views").insert({
                jurisprudencia_id: deepLinkId,
                user_id: user.id,
              }).then(() => {});
            }
          }
        });
      // Clean query param after opening
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, user, setSearchParams]);

  // Load saved IDs
  const loadSavedIds = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("jurisprudencia_saves")
      .select("jurisprudencia_id")
      .eq("user_id", user.id);
    if (data) setSavedIds(new Set(data.map((d) => d.jurisprudencia_id)));
  }, [user]);

  useEffect(() => {
    loadSavedIds();
  }, [loadSavedIds]);

  // Search
  const handleSearch = useCallback(async () => {
    setIsLoading(true);
    setHasSearched(true);

    // Log search
    if (user && searchQuery.trim()) {
      supabase.from("jurisprudencia_searches").insert({
        user_id: user.id,
        search_term: searchQuery.trim(),
        results_count: 0,
      }).then(() => {});
    }

    let query = supabase
      .from("jurisprudencias")
      .select("*, tribunais(sigla, nome)", { count: "exact" })
      .order("created_at", { ascending: false })
      .limit(50);

    if (tribunalFilter !== "all") {
      query = query.eq("tribunal_id", tribunalFilter);
    }

    if (searchQuery.trim()) {
      const term = `%${searchQuery.trim()}%`;
      query = query.or(`ementa.ilike.${term},numero_processo.ilike.${term},relator.ilike.${term},texto_completo.ilike.${term}`);
    }

    const { data, count, error } = await query;

    if (error) {
      toast.error("Erro na pesquisa");
      console.error(error);
    } else {
      setResults((data as any) || []);
      setTotalCount(count || 0);

      // Update search results count
      if (user && searchQuery.trim()) {
        supabase
          .from("jurisprudencia_searches")
          .update({ results_count: count || 0 })
          .eq("user_id", user.id)
          .eq("search_term", searchQuery.trim())
          .order("searched_at", { ascending: false })
          .limit(1)
          .then(() => {});
      }
    }

    setIsLoading(false);
  }, [searchQuery, tribunalFilter, user]);

  // No auto-search on mount - show landing page instead

  // Load saved items
  const loadSaved = useCallback(async () => {
    if (!user) return;
    const { data: saves } = await supabase
      .from("jurisprudencia_saves")
      .select("jurisprudencia_id")
      .eq("user_id", user.id);

    if (saves && saves.length > 0) {
      const ids = saves.map((s) => s.jurisprudencia_id);
      const { data } = await supabase
        .from("jurisprudencias")
        .select("*, tribunais(sigla, nome)")
        .in("id", ids);
      if (data) setSavedItems(data as any);
    } else {
      setSavedItems([]);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === "salvas") loadSaved();
  }, [activeTab, loadSaved]);

  // Toggle save
  const toggleSave = async (jurisprudenciaId: string) => {
    if (!user) return;

    if (savedIds.has(jurisprudenciaId)) {
      await supabase
        .from("jurisprudencia_saves")
        .delete()
        .eq("jurisprudencia_id", jurisprudenciaId)
        .eq("user_id", user.id);
      setSavedIds((prev) => {
        const next = new Set(prev);
        next.delete(jurisprudenciaId);
        return next;
      });
      toast.success("Removida dos salvos");
    } else {
      await supabase.from("jurisprudencia_saves").insert({
        jurisprudencia_id: jurisprudenciaId,
        user_id: user.id,
      });
      setSavedIds((prev) => new Set(prev).add(jurisprudenciaId));
      toast.success("Salva com sucesso!");
    }
  };

  // Track view
  const trackView = async (jurisprudenciaId: string) => {
    if (!user) return;
    await supabase.from("jurisprudencia_views").insert({
      jurisprudencia_id: jurisprudenciaId,
      user_id: user.id,
    });
  };

  const openItem = (item: JurisprudenciaItem) => {
    setSelectedItem(item);
    trackView(item.id);
  };

  // Ask AI about jurisprudencia
  const handleAskAI = (item: JurisprudenciaItem) => {
    const tribunal = item.tribunais ? `${item.tribunais.nome} (${item.tribunais.sigla})` : "";
    const relator = getRelator(item) !== "Não informado" ? `Relator: ${getRelator(item)}` : "";
    const data = item.data_julgamento
      ? `Data de Julgamento: ${format(new Date(item.data_julgamento), "dd/MM/yyyy", { locale: ptBR })}`
      : "";
    const orgao = item.orgao_julgador ? `Órgão Julgador: ${item.orgao_julgador}` : "";

    const context = [
      `Processo: ${item.numero_processo}`,
      tribunal,
      orgao,
      relator,
      data,
      `\nEmenta:\n${item.ementa}`,
      item.texto_completo ? `\nInteiro Teor:\n${item.texto_completo.substring(0, 3000)}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    const initialMessage = `Analise a seguinte jurisprudência e faça um resumo dos pontos principais, incluindo a tese jurídica, fundamentos e decisão:\n\n${context}`;

    navigate("/chat-juridico", {
      state: { initialMessage },
    });
  };

  // Open caso dialog
  const handleOpenCasoDialog = async (item: JurisprudenciaItem) => {
    if (!user) return;
    setShowCasoDialog(item);
    setLoadingCasos(true);
    const { data } = await supabase
      .from("casos")
      .select("id, title")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false });
    setCasos(data || []);
    setLoadingCasos(false);
  };

  // Add jurisprudencia to caso
  const handleAddToCaso = async (casoId: string) => {
    if (!user || !showCasoDialog) return;
    setAddingToCaso(true);

    const item = showCasoDialog;
    const tribunal = item.tribunais ? `${item.tribunais.nome} (${item.tribunais.sigla})` : "";
    const content = [
      `<h2>Jurisprudência - ${item.numero_processo}</h2>`,
      tribunal ? `<p><strong>Tribunal:</strong> ${tribunal}</p>` : "",
      item.orgao_julgador ? `<p><strong>Órgão Julgador:</strong> ${item.orgao_julgador}</p>` : "",
      getRelator(item) !== "Não informado" ? `<p><strong>Relator:</strong> ${getRelator(item)}</p>` : "",
      item.data_julgamento
        ? `<p><strong>Data de Julgamento:</strong> ${format(new Date(item.data_julgamento), "dd/MM/yyyy", { locale: ptBR })}</p>`
        : "",
      `<h3>Ementa</h3><p>${item.ementa}</p>`,
      item.texto_completo ? `<h3>Inteiro Teor</h3><p>${item.texto_completo}</p>` : "",
    ]
      .filter(Boolean)
      .join("\n");

    const { error } = await supabase.from("caso_documents").insert({
      caso_id: casoId,
      user_id: user.id,
      title: `Jurisprudência - ${item.numero_processo}`,
      type: "jurisprudencia",
      content,
    });

    if (error) {
      toast.error("Erro ao adicionar ao caso: " + error.message);
    } else {
      toast.success("Jurisprudência adicionada ao caso!");
      setShowCasoDialog(null);
    }
    setAddingToCaso(false);
  };

  const [showDetailsCard, setShowDetailsCard] = useState<string | null>(null);

  const renderJurisprudenciaCard = (item: JurisprudenciaItem) => {
    const isSaved = savedIds.has(item.id);
    const isDetailsOpen = showDetailsCard === item.id;

    return (
      <Card key={item.id} className="transition-all hover:shadow-md">
        <CardContent className="p-0">
          {/* Header bar - similar to reference image */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30 rounded-t-lg">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              {item.tribunais && (
                <span className="font-semibold text-foreground">{item.tribunais.sigla}</span>
              )}
              {item.orgao_julgador && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">{item.orgao_julgador}</span>
                </>
              )}
              {getRelator(item) !== "Não informado" && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">
                    <strong className="text-foreground/80">Relator</strong> · {getRelator(item)}
                  </span>
                </>
              )}
              {item.data_julgamento && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">
                    <strong className="text-foreground/80">Julgado em</strong>{" "}
                    {format(new Date(item.data_julgamento), "dd/MM/yyyy", { locale: ptBR })}
                  </span>
                </>
              )}
              <Button
                variant="link"
                size="sm"
                className="text-xs h-auto p-0 text-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetailsCard(isDetailsOpen ? null : item.id);
                }}
              >
                {isDetailsOpen ? "Mostrar menos" : "Mostrar mais"}
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => { e.stopPropagation(); toggleSave(item.id); }} title={isSaved ? "Remover dos salvos" : "Salvar"}>
                {isSaved ? <BookmarkCheck className="h-4 w-4 text-primary" /> : <Bookmark className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => { e.stopPropagation(); handleAskAI(item); }} title="Perguntar / Resumir com IA">
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => { e.stopPropagation(); handleOpenCasoDialog(item); }} title="Adicionar a um caso">
                <FolderPlus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Expandable details panel */}
          {isDetailsOpen && (
            <div className="px-4 py-3 border-b bg-background space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <Hash className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-foreground">Processo</p>
                    <p className="text-sm text-muted-foreground font-mono">{item.numero_processo}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-foreground">Órgão Julgador</p>
                    <p className="text-sm text-muted-foreground">{item.orgao_julgador || "Não informado"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CalendarDays className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-foreground">Data de julgamento</p>
                    <p className="text-sm text-muted-foreground">
                      {item.data_julgamento
                        ? format(new Date(item.data_julgamento), "dd/MM/yyyy", { locale: ptBR })
                        : "Não informada"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-foreground">Relator</p>
                    <p className="text-sm text-muted-foreground">{getRelator(item)}</p>
                  </div>
                </div>
                {item.tribunais && (
                  <div className="flex items-start gap-2">
                    <Gavel className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">Tribunal</p>
                      <p className="text-sm text-muted-foreground">{item.tribunais.nome} ({item.tribunais.sigla})</p>
                    </div>
                  </div>
                )}
                {item.area_direito && (
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">Área do Direito</p>
                      <p className="text-sm text-muted-foreground">{item.area_direito}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Ementa + actions */}
          <div className="p-4 space-y-3 cursor-pointer" onClick={() => openItem(item)}>
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">
                💬 Ementa
              </Badge>
              {item.palavras_chave && item.palavras_chave.length > 0 && item.palavras_chave.slice(0, 3).map((kw, i) => (
                <Badge key={i} variant="outline" className="text-[10px]">{kw}</Badge>
              ))}
            </div>
            <p className="text-sm leading-relaxed line-clamp-3">{item.ementa}</p>
          </div>
        </CardContent>
      </Card>
    );
  };

  const showLanding = !hasSearched && activeTab === "pesquisar";

  return (
    <div className="min-h-[80vh] flex flex-col">
      {showLanding ? (
        /* ===== Landing / Hero Section ===== */
        <section className="flex-1 flex flex-col items-center justify-center px-4 min-h-0">
          <div className="text-center max-w-2xl mx-auto animate-fade-in">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary">
              <Scale className="h-8 w-8 text-primary-foreground" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              Pesquisa Jurídica Nacional
            </h1>
            <p className="text-lg text-muted-foreground mb-10">
              Busque jurisprudências de tribunais de todo o Brasil em uma única plataforma.
            </p>

            <form
              className="space-y-4 text-left"
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch();
              }}
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  className="flex w-full border border-input bg-background px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm pl-12 h-14 text-base rounded-xl"
                  placeholder="Buscar por número do processo ou palavras da ementa..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button
                className="w-full rounded-xl h-12"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 mr-2" />
                )}
                Pesquisar Jurisprudências
              </Button>
            </form>

            {/* Salvas button always visible */}
            <div className="mt-6">
              <Button
                variant="outline"
                onClick={() => setActiveTab("salvas")}
                className="gap-1.5"
              >
                <Bookmark className="h-4 w-4" />
                Salvas ({savedIds.size})
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 mt-16 animate-fade-in">
            <div className="text-center">
              <BookOpen className="h-8 w-8 mx-auto mb-2 text-accent" />
              <p className="text-2xl font-heading font-bold text-foreground">Jurisprudências</p>
              <p className="text-sm text-muted-foreground">Decisões de tribunais brasileiros</p>
            </div>
            <div className="text-center">
              <Building2 className="h-8 w-8 mx-auto mb-2 text-accent" />
              <p className="text-2xl font-heading font-bold text-foreground">Tribunais</p>
              <p className="text-sm text-muted-foreground">Cobertura nacional</p>
            </div>
          </div>
        </section>
      ) : (
        /* ===== Results / Standard View ===== */
        <div className="container mx-auto p-6 space-y-6">
          <div data-tour="jurisprudencias-header">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
              <Scale className="h-7 w-7" />
              Jurisprudências
            </h1>
            <p className="text-muted-foreground">
              Pesquise e gerencie jurisprudências integradas aos seus casos.
            </p>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-tour="jurisprudencias-tabs">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as JurisprudenciaTab)}>
              <TabsList>
                <TabsTrigger value="pesquisar" className="gap-1.5" data-tour="jurisprudencias-tab-pesquisar">
                  <Search className="h-3.5 w-3.5" />
                  Pesquisar
                </TabsTrigger>
                <TabsTrigger value="salvas" className="gap-1.5" data-tour="jurisprudencias-tab-salvas">
                  <Bookmark className="h-3.5 w-3.5" />
                  Salvas ({savedIds.size})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {activeTab === "pesquisar" && (
            <div className="space-y-4">
              {/* Search bar */}
              <div className="flex gap-2" data-tour="jurisprudencias-search">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Pesquise por ementa, número do processo, relator..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pl-10"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => { setSearchQuery(""); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
                <Select value={tribunalFilter} onValueChange={setTribunalFilter}>
                  <SelectTrigger className="w-[160px]">
                    <Filter className="h-4 w-4 mr-1" />
                    <SelectValue placeholder="Tribunal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {tribunais.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.sigla}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleSearch} disabled={isLoading}>
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  <span className="ml-1 hidden sm:inline">Pesquisar</span>
                </Button>
              </div>

              {/* Results count */}
              {hasSearched && (
                <p className="text-sm text-muted-foreground">
                  {totalCount} resultado{totalCount !== 1 ? "s" : ""} encontrado{totalCount !== 1 ? "s" : ""}
                  {searchQuery && ` para "${searchQuery}"`}
                </p>
              )}

              {/* Results */}
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : results.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Scale className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-1">Nenhum resultado encontrado</h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    Tente usar termos diferentes ou remova os filtros.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {results.map(renderJurisprudenciaCard)}
                </div>
              )}
            </div>
          )}

          {activeTab === "salvas" && (
            <div className="space-y-3">
              {savedItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Bookmark className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-1">Nenhuma jurisprudência salva</h3>
                  <p className="text-sm text-muted-foreground">
                    Salve jurisprudências durante a pesquisa para acessá-las rapidamente aqui.
                  </p>
                </div>
              ) : (
                savedItems.map(renderJurisprudenciaCard)
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de leitura completa */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedItem && (
            <>
              <DialogHeader className="shrink-0">
                <div className="flex items-center gap-2 flex-wrap text-sm mb-2">
                  {selectedItem.tribunais && (
                    <span className="font-semibold">{selectedItem.tribunais.sigla}</span>
                  )}
                  {selectedItem.orgao_julgador && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">{selectedItem.orgao_julgador}</span>
                    </>
                  )}
                  {getRelator(selectedItem) !== "Não informado" && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">Relator · {getRelator(selectedItem)}</span>
                    </>
                  )}
                  {selectedItem.data_julgamento && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">
                        Julgado em {format(new Date(selectedItem.data_julgamento), "dd/MM/yyyy", { locale: ptBR })}
                      </span>
                    </>
                  )}
                </div>

                <DialogTitle className="text-lg">
                  Detalhes da jurisprudência
                </DialogTitle>
                <p className="text-sm text-muted-foreground">Dados oficiais e contexto do julgamento.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t mt-3">
                  <div className="flex items-start gap-2">
                    <Hash className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">Processo</p>
                      <p className="text-sm text-muted-foreground font-mono">{selectedItem.numero_processo}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Building2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">Órgão Julgador</p>
                      <p className="text-sm text-muted-foreground">{selectedItem.orgao_julgador || "Não informado"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CalendarDays className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">Data de julgamento</p>
                      <p className="text-sm text-muted-foreground">
                        {selectedItem.data_julgamento
                          ? format(new Date(selectedItem.data_julgamento), "dd/MM/yyyy", { locale: ptBR })
                          : "Não informada"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">Relator</p>
                      <p className="text-sm text-muted-foreground">{getRelator(selectedItem)}</p>
                    </div>
                  </div>
                  {selectedItem.tribunais && (
                    <div className="flex items-start gap-2">
                      <Gavel className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                      <div>
                        <p className="text-xs font-semibold">Tribunal</p>
                        <p className="text-sm text-muted-foreground">{selectedItem.tribunais.nome} ({selectedItem.tribunais.sigla})</p>
                      </div>
                    </div>
                  )}
                  {selectedItem.area_direito && (
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                      <div>
                        <p className="text-xs font-semibold">Área do Direito</p>
                        <p className="text-sm text-muted-foreground">{selectedItem.area_direito}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-3 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => toggleSave(selectedItem.id)}>
                    {savedIds.has(selectedItem.id) ? (
                      <><BookmarkCheck className="h-4 w-4 mr-1 text-primary" /> Salva</>
                    ) : (
                      <><Bookmark className="h-4 w-4 mr-1" /> Salvar</>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleAskAI(selectedItem)}>
                    <MessageSquare className="h-4 w-4 mr-1" /> Perguntar / Resumir com IA
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleOpenCasoDialog(selectedItem)}>
                    <FolderPlus className="h-4 w-4 mr-1" /> Adicionar a um caso
                  </Button>
                  {selectedItem.palavras_chave?.slice(0, 5).map((kw, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                  ))}
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto mt-4 space-y-4">
                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Ementa</h4>
                  <p className="text-sm leading-relaxed">{selectedItem.ementa}</p>
                </div>
                {selectedItem.texto_completo && (
                  <div className="border-t pt-4">
                    <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Inteiro Teor</h4>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-md p-4">
                      {selectedItem.texto_completo}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para adicionar a um caso */}
      <Dialog open={!!showCasoDialog} onOpenChange={(open) => !open && setShowCasoDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar a um caso</DialogTitle>
            <DialogDescription>
              Selecione o caso onde deseja incluir esta jurisprudência.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {loadingCasos ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : casos.length === 0 ? (
              <div className="text-center py-8">
                <FolderPlus className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">Você ainda não tem casos criados.</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => { setShowCasoDialog(null); navigate("/casos"); }}>
                  Criar um caso
                </Button>
              </div>
            ) : (
              casos.map((caso) => (
                <Button
                  key={caso.id}
                  variant="outline"
                  className="w-full justify-start text-left h-auto py-3"
                  disabled={addingToCaso}
                  onClick={() => handleAddToCaso(caso.id)}
                >
                  <FolderPlus className="h-4 w-4 mr-2 shrink-0" />
                  <span className="truncate">{caso.title}</span>
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
